import { History } from 'history';
import qs from 'qs';
import { PartialRecord } from './types';

export function getBaseURL() {
  const { protocol, hostname, port } = window.location;

  return `${protocol}//${hostname}${port ? ':' + port : ''}`;
}

export function buildQueryString(params: any, addPrefix = false) {
  const qs = Object.keys(params || {})
    .filter((key) => typeof params[key] !== 'undefined')
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');

  if (!addPrefix || !qs.length) {
    return qs;
  }

  return `?${qs}`;
}

export function getQueryParam(
  param: string,
  defaultValue: any = null,
  parser?: (input: string) => any,
): any {
  const qs = parseQueryString(window.location.search);

  return qs[param] ? (parser ? parser(qs[param]) : qs[param]) : defaultValue;
}

export function parseQueryString(
  query: string,
  cast = false,
  parsers: PartialRecord<string, (input: string) => any> = {},
): Record<string, any> {
  const parsedQuery = qs.parse(query.replace(/^\?/, ''));

  let beforeParsers: Record<string, any>;

  if (!cast) {
    beforeParsers = parsedQuery;
  } else {
    beforeParsers = Object.keys(parsedQuery).reduce((all: any, key) => {
      let value = parsedQuery[key] as any;

      if (value === 'true') {
        value = true;
      } else if (value === 'false') {
        value = false;
      } else if (/^\d+$/.test(value)) {
        value = Number(value);
      }

      all[key] = value;

      return all;
    }, {});
  }

  return Object.keys(beforeParsers).reduce((all: any, key) => {
    if (beforeParsers[key] === '') {
      return all;
    }

    if (parsers[key]) {
      all[key] = parsers[key]!(beforeParsers[key]);
    } else {
      all[key] = beforeParsers[key];
    }

    return all;
  }, {});
}

export function appendQueryParam(url: any, param: any, value: any) {
  const hasQueryString = url.match(/\?/);

  return (
    url + (hasQueryString ? `&` : '?') + `${encodeURIComponent(param)}=${encodeURIComponent(value)}`
  );
}

export function cleanQuery<T extends object>(query: T): T {
  return (Object.keys(query) as (keyof typeof query)[]).reduce((all, key) => {
    if (
      (typeof key === 'string' && key.startsWith('_')) ||
      (query[key] as any) === '' ||
      query[key] === null ||
      typeof query[key] === 'undefined'
    ) {
      return all;
    }

    return {
      ...all,
      [key]: query[key],
    };
  }, {}) as T;
}

export function downloadFile(url: string, newTab = true) {
  if (newTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
}

export function updateQueryParam(
  history: History,
  param: string,
  value: string | number,
  doPush = true,
) {
  const qs = parseQueryString(window.location.search);

  const updatedParams = {
    ...qs,
    [param]: value,
  };

  if (typeof value === 'undefined' || value === '') {
    delete updatedParams[param];
  }

  const params = {
    pathname: window.location.pathname,
    search: buildQueryString(updatedParams),
  };

  if (doPush) {
    history.push(params);
  } else {
    history.replace(params);
  }
}

export function setQuery(history: History, query: any) {
  history.push({
    pathname: window.location.pathname,
    search: buildQueryString(cleanQuery(query)),
  });
}

export function updateQuery(history: History, query: any) {
  const current = parseQueryString(window.location.search);

  const updatedParams = cleanQuery({
    ...current,
    ...query,
  });

  history.push({
    pathname: window.location.pathname,
    search: buildQueryString(updatedParams),
  });
}

export function downloadBlob(blob: Blob, filename: string) {
  // Create an object URL for the blob object
  const url = URL.createObjectURL(blob);

  // Create a new anchor element
  const a = document.createElement('a');

  // Set the href and download attributes for the anchor element
  // You can optionally set other attributes like `title`, etc
  // Especially, if the anchor element will be attached to the DOM
  a.href = url;
  a.download = filename || 'download';

  // Click handler that releases the object URL after the element has been clicked
  // This is required for one-off downloads of the blob content
  const clickHandler = function (this: any) {
    setTimeout(() => {
      URL.revokeObjectURL(url);
      this.removeEventListener('click', clickHandler);
    }, 150);
  };

  // Add the click event listener on the anchor element
  // Comment out this line if you don't want a one-off download of the blob content
  a.addEventListener('click', clickHandler, false);

  // Programmatically trigger a click on the anchor element
  // Useful if you want the download to happen automatically
  // Without attaching the anchor element to the DOM
  // Comment out this line if you don't want an automatic download of the blob content
  a.click();

  // Return the anchor element
  // Useful if you want a reference to the element
  // in order to attach it to the DOM or use it in some other way
  return a;
}
